import { type MouseEvent, type ReactNode, useEffect, useMemo, useState } from "react";
import { useBusiness } from "../../lib/business.js";
import { useMe } from "../auth/api.js";
import { type FunnelRow, useFunnel, useSyncAds } from "./api.js";
import styles from "./CampaignsScreen.module.css";
import { Funnel, GroupedBars } from "./charts.js";

// Campaigns (doc 02 §2) — Click-to-WhatsApp ad performance. Range chip → 4 KPI cards
// → analytics band (spend-vs-revenue · by-platform · funnel · ROAS highlights) →
// Excel-style ad table with click-to-sort headers + Columns picker + per-column
// filter funnels. KPIs/trend/funnel scope to the range; By-platform + ROAS highlights
// are an all-campaigns snapshot (independent of the range). Empty/"—"/0 states are the
// expected staging case (no Meta data).

const fmtRM = (n: number) => `RM${Math.round(n).toLocaleString()}`;
const cls = (r: number) => (r >= 3 ? "good" : r >= 1.2 ? "ok" : "bad");
const label = (r: number) => (r >= 3 ? "Strong" : r >= 1.2 ? "OK" : "Weak");

const PLATFORM_COLORS: Record<string, string> = {
  Instagram: "#E1306C",
  Facebook: "#1877F2",
  TikTok: "#00C4CC",
  WhatsApp: "#25D366",
  Messenger: "#A033FF",
};
const platColor = (p: string) => PLATFORM_COLORS[p] ?? "var(--mist)";

// MYT (UTC+8) ISO date for `days` ago … today — matches the server's day bucketing.
function mytDaysAgo(days: number): string {
  return new Date(Date.now() + 8 * 3_600_000 - days * 86_400_000).toISOString().slice(0, 10);
}

const ICON_CALENDAR = (
  <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
    <rect x="3.5" y="4.5" width="13" height="12" rx="1.5" stroke="currentColor" strokeWidth="1.4" />
    <path
      d="M3.5 8h13M7 3v3M13 3v3"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
    />
  </svg>
);
const ICON_CHEVRON = (
  <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
    <path
      d="M5.5 8l4.5 4.5L14.5 8"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);
const ICON_CHECK = (
  <svg viewBox="0 0 14 14" fill="none" aria-hidden="true">
    <path
      d="M2.5 7.5l3 3 6-7"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);
const ICON_SEARCH = (
  <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
    <circle cx="9" cy="9" r="5.5" stroke="currentColor" strokeWidth="1.5" />
    <path d="M13.5 13.5l3 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);
const ICON_COLUMNS = (
  <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
    <rect x="3" y="4" width="14" height="12" rx="1.5" stroke="currentColor" strokeWidth="1.4" />
    <path d="M9 4v12M13.5 4v12" stroke="currentColor" strokeWidth="1.4" />
  </svg>
);
const ICON_FUNNEL = (
  <svg viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path
      d="M2 3.5h12l-4.5 5v4l-3 1.5v-5.5L2 3.5z"
      stroke="currentColor"
      strokeWidth="1.3"
      strokeLinejoin="round"
    />
  </svg>
);

type ColKey =
  | "ad"
  | "campaign"
  | "adset"
  | "platform"
  | "status"
  | "spend"
  | "chats"
  | "cpc"
  | "orders"
  | "conv"
  | "revenue"
  | "roas";

type FilterKey = "platform" | "status";

interface Column {
  key: ColKey;
  label: string;
  anchor?: boolean;
  num?: boolean;
  filterKey?: FilterKey;
  sort: (r: FunnelRow) => number | string;
  cell: (r: FunnelRow) => ReactNode;
}

const COLUMNS: Column[] = [
  {
    key: "ad",
    label: "Ads",
    anchor: true,
    sort: (a) => a.adName.toLowerCase(),
    cell: (a) => (
      <td className={styles.strong} key="ad">
        {a.adName}
        {a.status === "paused" ? <span className={styles.adPaused}>paused</span> : null}
      </td>
    ),
  },
  {
    key: "campaign",
    label: "Campaign",
    sort: (a) => (a.campaignName ?? "").toLowerCase(),
    cell: (a) => (
      <td className={styles.mono} key="campaign">
        {a.campaignName ?? "—"}
      </td>
    ),
  },
  {
    key: "adset",
    label: "Ad set",
    sort: (a) => (a.adSet ?? "").toLowerCase(),
    cell: (a) => (
      <td className={`${styles.mono} ${styles.dim}`} key="adset">
        {a.adSet ?? "—"}
      </td>
    ),
  },
  {
    key: "platform",
    label: "Platform",
    filterKey: "platform",
    sort: (a) => (a.platform ?? "").toLowerCase(),
    cell: (a) => (
      <td className={`${styles.mono} ${styles.dim}`} key="platform">
        {a.platform ?? "—"}
      </td>
    ),
  },
  {
    key: "status",
    label: "Status",
    filterKey: "status",
    sort: (a) => a.status,
    cell: (a) => (
      <td key="status">
        <span className={`${styles.campStatus} ${a.status === "paused" ? styles.off : styles.on}`}>
          {a.status === "paused" ? "Paused" : "Active"}
        </span>
      </td>
    ),
  },
  {
    key: "spend",
    label: "Spend",
    num: true,
    sort: (a) => a.spend,
    cell: (a) => (
      <td className={styles.mono} key="spend">
        {fmtRM(a.spend)}
      </td>
    ),
  },
  {
    key: "chats",
    label: "Chats",
    num: true,
    sort: (a) => a.conversations,
    cell: (a) => (
      <td className={styles.mono} key="chats">
        {a.conversations}
      </td>
    ),
  },
  {
    key: "cpc",
    label: "Cost/chat",
    num: true,
    sort: (a) => a.cpc,
    cell: (a) => (
      <td className={`${styles.mono} ${styles.dim}`} key="cpc">
        {fmtRM(a.cpc)}
      </td>
    ),
  },
  {
    key: "orders",
    label: "Orders",
    num: true,
    sort: (a) => a.orders,
    cell: (a) => (
      <td className={styles.mono} key="orders">
        {a.orders}
      </td>
    ),
  },
  {
    key: "conv",
    label: "Conv.",
    num: true,
    sort: (a) => a.conv,
    cell: (a) => (
      <td className={`${styles.mono} ${styles.dim}`} key="conv">
        {a.conv.toFixed(0)}%
      </td>
    ),
  },
  {
    key: "revenue",
    label: "Revenue",
    num: true,
    sort: (a) => a.revenue,
    cell: (a) => (
      <td className={styles.mono} key="revenue">
        {fmtRM(a.revenue)}
      </td>
    ),
  },
  {
    key: "roas",
    label: "ROAS",
    num: true,
    sort: (a) => a.roas ?? 0,
    cell: (a) => {
      const r = a.roas ?? 0;
      return (
        <td key="roas">
          <span className={`${styles.roas} ${styles[cls(r)]}`}>
            {r.toFixed(1)}× · {label(r)}
          </span>
        </td>
      );
    },
  },
];

const STATUS_LABEL: Record<string, string> = { active: "Active", paused: "Paused" };

export function CampaignsScreen() {
  const { activeId } = useBusiness();
  // Remount per business so all table/filter UI state resets on switch.
  return <CampaignsBody key={activeId} activeId={activeId} />;
}

function CampaignsBody({ activeId }: { activeId: string }) {
  const me = useMe();
  const isAdmin = me.data?.role === "admin";
  const syncAds = useSyncAds(activeId);
  // KPI/trend date range: "8w" | "4w" | "custom".
  const [range, setRange] = useState<"8w" | "4w" | "custom">("8w");
  const [rangeMenu, setRangeMenu] = useState(false);
  const [cFrom, setCFrom] = useState("");
  const [cTo, setCTo] = useState("");

  // Resolve the active range to from/to ISO dates for the server query.
  const { from, to, rangeShort } = useMemo(() => {
    if (range === "custom" && cFrom && cTo) {
      const lo = cFrom <= cTo ? cFrom : cTo;
      const hi = cFrom <= cTo ? cTo : cFrom;
      const dmy = (s: string) =>
        new Date(`${s}T00:00:00Z`).toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
      return { from: lo, to: hi, rangeShort: `${dmy(lo)} – ${dmy(hi)}` };
    }
    if (range === "4w")
      return { from: mytDaysAgo(28), to: mytDaysAgo(0), rangeShort: "Last 4 weeks" };
    return { from: mytDaysAgo(56), to: mytDaysAgo(0), rangeShort: "Last 8 weeks" };
  }, [range, cFrom, cTo]);

  const funnel = useFunnel(activeId, from, to);
  const totals = funnel.data?.totals;
  const rows = funnel.data?.rows ?? [];
  const trend = funnel.data?.trend ?? [];
  const byPlatform = funnel.data?.byPlatform ?? [];
  const rankedAds = funnel.data?.rankedAds ?? [];

  // Map the weekly trend series into GroupedBars rows (Spend vs Revenue).
  const trendBars = useMemo(
    () =>
      trend.map((w) => ({
        id: w.weekStart,
        label: w.weekLabel,
        values: { spend: w.spend, revenue: w.revenue },
      })),
    [trend],
  );

  // Custom-range bounds: full available window (last 8 weeks) in MYT ISO.
  const minDate = mytDaysAgo(56);
  const maxDate = mytDaysAgo(0);

  const aSpend = totals?.spend ?? 0;
  const aChats = totals?.conversations ?? 0;
  const aOrders = totals?.orders ?? 0;
  const aRevenue = totals?.revenue ?? 0;
  const aClicks = totals?.clicks ?? 0;
  const aRoas = totals?.roas ?? 0;

  const funnelSteps = [
    { label: "Ad clicks", value: aClicks, color: "var(--mist)" },
    { label: "Chats opened", value: aChats, color: "var(--azure)" },
    { label: "Orders", value: aOrders, color: "var(--teal)" },
  ];
  const platMaxRev = Math.max(1, ...byPlatform.map((p) => p.revenue));
  const best = rankedAds[0] ?? null;
  const worst = rankedAds.length > 1 ? rankedAds[rankedAds.length - 1] : null;
  const platforms = useMemo(
    () => Array.from(new Set(rows.map((r) => r.platform).filter((p): p is string => !!p))),
    [rows],
  );

  // ---- table state: search · per-column filters · sort · columns ----
  const [q, setQ] = useState("");
  const [colFilters, setColFilters] = useState<Record<FilterKey, Set<string>>>({
    platform: new Set(),
    status: new Set(),
  });
  const [sort, setSort] = useState<{ key: ColKey; dir: "asc" | "desc" }>({
    key: "roas",
    dir: "desc",
  });
  const [visible, setVisible] = useState<Set<ColKey>>(() => new Set(COLUMNS.map((c) => c.key)));
  const [colMenu, setColMenu] = useState(false);
  const [showAllPlat, setShowAllPlat] = useState(false);
  const [hf, setHf] = useState<{ key: FilterKey; x: number; y: number } | null>(null);

  const FILTER_VALUES: Record<FilterKey, string[]> = {
    platform: platforms,
    status: ["active", "paused"],
  };

  // Close any popover on Escape (cross-screen invariant).
  useEffect(() => {
    if (!rangeMenu && !colMenu && !hf) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setRangeMenu(false);
        setColMenu(false);
        setHf(null);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [rangeMenu, colMenu, hf]);

  const toggleCol = (key: ColKey) =>
    setVisible((s) => {
      const n = new Set(s);
      if (n.has(key)) n.delete(key);
      else n.add(key);
      return n;
    });
  const shownCols = COLUMNS.filter((c) => c.anchor || visible.has(c.key));
  const toggleSort = (key: ColKey) => {
    const c = COLUMNS.find((x) => x.key === key);
    setSort((s) =>
      s.key === key
        ? { key, dir: s.dir === "asc" ? "desc" : "asc" }
        : { key, dir: c?.num ? "desc" : "asc" },
    );
  };
  const toggleCF = (kind: FilterKey, val: string) =>
    setColFilters((prev) => {
      const set = new Set(prev[kind]);
      if (set.has(val)) set.delete(val);
      else set.add(val);
      return { ...prev, [kind]: set };
    });
  const clearCF = (kind: FilterKey) =>
    setColFilters((prev) => ({ ...prev, [kind]: new Set<string>() }));
  const openHF = (key: FilterKey) => (e: MouseEvent<HTMLButtonElement>) => {
    const r = e.currentTarget.getBoundingClientRect();
    const x = Math.max(8, Math.min(r.left - 60, (window.innerWidth || 1200) - 196));
    setHf((cur) => (cur && cur.key === key ? null : { key, x, y: r.bottom + 6 }));
  };

  // ---- table rows: filter then sort (independent of the analytics band, doc 02 §2.4) ----
  const tableRows = useMemo(() => {
    const ql = q.trim().toLowerCase();
    let out = rows.filter((a) => {
      if (colFilters.platform.size && !(a.platform && colFilters.platform.has(a.platform)))
        return false;
      if (colFilters.status.size && !colFilters.status.has(a.status)) return false;
      if (ql && !`${a.adName} ${a.campaignName ?? ""} ${a.adSet ?? ""}`.toLowerCase().includes(ql))
        return false;
      return true;
    });
    const sortCol = COLUMNS.find((c) => c.key === sort.key);
    if (sortCol) {
      out = [...out].sort((x, y) => {
        const a = sortCol.sort(x);
        const b = sortCol.sort(y);
        const r =
          typeof a === "number" && typeof b === "number"
            ? a - b
            : String(a).localeCompare(String(b));
        return sort.dir === "asc" ? r : -r;
      });
    }
    return out;
  }, [rows, q, colFilters, sort]);

  return (
    <div className={styles.screen}>
      <div className={styles.screenHead}>
        <div>
          <h1 className={styles.cpH}>Campaigns</h1>
          <p className={styles.cpSub}>
            Click-to-WhatsApp ad performance · attributed to chats &amp; orders
          </p>
        </div>
        <div className={styles.rangeWrap}>
          {isAdmin && (
            <button
              type="button"
              className={styles.chipBtn}
              disabled={syncAds.isPending}
              title="Pull every WhatsApp-CTA ad (incl. paused history) + 90 days of spend from Meta"
              onClick={() => syncAds.mutate()}
            >
              {syncAds.isPending ? "Syncing…" : "Sync ads"}
            </button>
          )}
          <button
            type="button"
            className={`${styles.chipBtn} ${styles.rangeBtn}`}
            onClick={() => setRangeMenu((v) => !v)}
            aria-expanded={rangeMenu}
          >
            {ICON_CALENDAR}
            {rangeShort}
            {ICON_CHEVRON}
          </button>
          {rangeMenu && (
            <>
              <button
                type="button"
                className={styles.menuCatch}
                aria-label="Close range menu"
                onClick={() => setRangeMenu(false)}
              />
              <div className={`${styles.filterMoreMenu} ${styles.rangeMenu}`}>
                <button
                  type="button"
                  className={`${styles.moreOpt} ${range === "4w" ? styles.optOn : ""}`}
                  onClick={() => {
                    setRange("4w");
                    setRangeMenu(false);
                  }}
                >
                  <span className={styles.colCheck}>{range === "4w" ? ICON_CHECK : null}</span>
                  Last 4 weeks
                </button>
                <button
                  type="button"
                  className={`${styles.moreOpt} ${range === "8w" ? styles.optOn : ""}`}
                  onClick={() => {
                    setRange("8w");
                    setRangeMenu(false);
                  }}
                >
                  <span className={styles.colCheck}>{range === "8w" ? ICON_CHECK : null}</span>
                  Last 8 weeks
                </button>
                <div className={styles.rangeCustom}>
                  <div className={styles.rangeCustomH}>Custom range</div>
                  <label className={styles.rangeDt}>
                    From
                    <input
                      type="date"
                      min={minDate}
                      max={maxDate}
                      value={cFrom}
                      onChange={(e) => {
                        setCFrom(e.target.value);
                        setRange("custom");
                      }}
                    />
                  </label>
                  <label className={styles.rangeDt}>
                    To
                    <input
                      type="date"
                      min={minDate}
                      max={maxDate}
                      value={cTo}
                      onChange={(e) => {
                        setCTo(e.target.value);
                        setRange("custom");
                      }}
                    />
                  </label>
                  {range === "custom" && (!cFrom || !cTo) && (
                    <div className={styles.rangeHint}>Pick both dates.</div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      <div className={styles.statGrid}>
        <div className={styles.stat}>
          <div className={styles.lbl}>Ad spend</div>
          <div className={`${styles.big} ${styles.tnum}`}>{fmtRM(aSpend)}</div>
          <div className={styles.statRange}>{rangeShort}</div>
        </div>
        <div className={styles.stat}>
          <div className={styles.lbl}>Chats from ads</div>
          <div className={`${styles.big} ${styles.tnum}`}>{aChats.toLocaleString()}</div>
          <div className={styles.statRange}>{rangeShort}</div>
        </div>
        <div className={styles.stat}>
          <div className={styles.lbl}>Orders</div>
          <div className={`${styles.big} ${styles.tnum}`}>{aOrders}</div>
          <div className={styles.statRange}>{rangeShort}</div>
        </div>
        <div className={styles.stat}>
          <div className={styles.lbl}>Revenue · ROAS</div>
          <div className={`${styles.big} ${styles.good} ${styles.tnum}`}>
            {fmtRM(aRevenue)} <span className={styles.sm}>{aRoas.toFixed(1)}×</span>
          </div>
          <div className={styles.statRange}>{rangeShort}</div>
        </div>
      </div>

      {/* analytics overview — does NOT react to table filters */}
      <div className={`${styles.cpCols} ${styles.campAnRow}`}>
        <section className={`${styles.panel} ${styles.anPanel}`}>
          <div className={styles.panelH}>
            <span className={styles.t}>Spend vs attributed revenue</span>
            <span className={styles.meta}>{rangeShort.toLowerCase()}</span>
          </div>
          <div className={styles.anBody}>
            {trendBars.length ? (
              <GroupedBars
                data={trendBars}
                fmt={fmtRM}
                series={[
                  { key: "spend", label: "Spend", color: "var(--mist-dim)" },
                  { key: "revenue", label: "Revenue", color: "var(--teal)" },
                ]}
              />
            ) : (
              <div className={styles.anEmpty}>No trend data.</div>
            )}
          </div>
        </section>
        <section className={`${styles.panel} ${styles.anPanel}`}>
          <div className={styles.panelH}>
            <span className={styles.t}>By platform</span>
            <span className={styles.meta}>all campaigns · revenue share</span>
          </div>
          <div className={styles.anBody}>
            {byPlatform.length ? (
              (showAllPlat ? byPlatform : byPlatform.slice(0, 3)).map((p) => {
                const r = p.roas ?? 0;
                return (
                  <div className={styles.platRow} key={p.platform}>
                    <div className={styles.platTop}>
                      <span className={styles.platName}>
                        <span
                          className={styles.platDot}
                          style={{ background: platColor(p.platform) }}
                        />
                        {p.platform}
                      </span>
                      <span className={`${styles.platRev} ${styles.mono}`}>
                        {fmtRM(p.revenue)}{" "}
                        <span className={`${styles.roas} ${styles[cls(r)]}`}>{r.toFixed(1)}×</span>
                      </span>
                    </div>
                    <div className={styles.platTrack}>
                      <div
                        className={styles.platFill}
                        style={{
                          width: `${Math.max(3, (p.revenue / platMaxRev) * 100)}%`,
                          background: platColor(p.platform),
                        }}
                      />
                    </div>
                    <div className={`${styles.platSub} ${styles.mono}`}>
                      {p.chats} chats · {p.orders} orders · {fmtRM(p.spend)} spend
                    </div>
                  </div>
                );
              })
            ) : (
              <div className={styles.anEmpty}>No attributed ads yet.</div>
            )}
            {byPlatform.length > 3 && (
              <button
                type="button"
                className={styles.platMore}
                onClick={() => setShowAllPlat((v) => !v)}
              >
                {showAllPlat ? "Show fewer" : `Show all ${byPlatform.length} platforms`}
              </button>
            )}
          </div>
        </section>
      </div>

      <div className={`${styles.cpCols} ${styles.campAnRow}`}>
        <section className={`${styles.panel} ${styles.anPanel}`}>
          <div className={styles.panelH}>
            <span className={styles.t}>Chats → orders</span>
            <span className={styles.meta}>{rangeShort.toLowerCase()} · funnel</span>
          </div>
          <div className={styles.anBody}>
            <Funnel steps={funnelSteps} />
          </div>
        </section>
        <section className={`${styles.panel} ${styles.anPanel}`}>
          <div className={styles.panelH}>
            <span className={styles.t}>ROAS highlights</span>
            <span className={styles.meta}>all campaigns · best &amp; worst</span>
          </div>
          <div className={`${styles.anBody} ${styles.roasHi}`}>
            {best && (
              <div className={`${styles.roasCard} ${styles.good}`}>
                <div className={styles.rcTag}>Top performer</div>
                <div className={styles.rcName}>{best.adName}</div>
                <div className={`${styles.rcMeta} ${styles.mono}`}>
                  {best.campaignName ?? "—"} · {best.platform ?? "—"}
                </div>
                <div className={styles.rcRoas}>
                  <span className={`${styles.roas} ${styles[cls(best.roas)]}`}>
                    {best.roas.toFixed(1)}×
                  </span>
                  <span className={`${styles.rcRev} ${styles.mono}`}>
                    {fmtRM(best.revenue)} on {fmtRM(best.spend)}
                  </span>
                </div>
              </div>
            )}
            {worst && (
              <div className={`${styles.roasCard} ${styles.bad}`}>
                <div className={styles.rcTag}>Needs attention</div>
                <div className={styles.rcName}>{worst.adName}</div>
                <div className={`${styles.rcMeta} ${styles.mono}`}>
                  {worst.campaignName ?? "—"} · {worst.platform ?? "—"}
                </div>
                <div className={styles.rcRoas}>
                  <span className={`${styles.roas} ${styles[cls(worst.roas)]}`}>
                    {worst.roas.toFixed(1)}×
                  </span>
                  <span className={`${styles.rcRev} ${styles.mono}`}>
                    {fmtRM(worst.revenue)} on {fmtRM(worst.spend)}
                  </span>
                </div>
              </div>
            )}
            {!best && <div className={styles.anEmpty}>No attributed ads yet.</div>}
          </div>
        </section>
      </div>

      {/* ad table: slim toolbar; sort + filter live in the header */}
      <div className={`${styles.toolbar} ${styles.campToolbar}`}>
        <div className={styles.searchbox}>
          {ICON_SEARCH}
          <input
            placeholder="Search ad, campaign or ad set…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
        <div className={`${styles.filterMoreWrap} ${styles.campCols}`}>
          <button
            type="button"
            className={`${styles.chipBtn} ${styles.colsBtn}`}
            onClick={() => setColMenu((v) => !v)}
            aria-expanded={colMenu}
          >
            {ICON_COLUMNS}
            Columns
          </button>
          {colMenu && (
            <>
              <button
                type="button"
                className={styles.menuCatch}
                aria-label="Close columns menu"
                onClick={() => setColMenu(false)}
              />
              <div className={`${styles.filterMoreMenu} ${styles.colMenu}`}>
                <div className={styles.colMenuH}>Show columns</div>
                {COLUMNS.map((c) => {
                  const on = c.anchor || visible.has(c.key);
                  return (
                    <button
                      key={c.key}
                      type="button"
                      className={`${styles.moreOpt} ${styles.colOpt} ${on ? styles.optOn : ""}`}
                      disabled={c.anchor}
                      onClick={() => !c.anchor && toggleCol(c.key)}
                    >
                      <span className={styles.colCheck}>{on ? ICON_CHECK : null}</span>
                      {c.label}
                      {c.anchor ? <span className={styles.colLock}>fixed</span> : null}
                    </button>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>

      <div className={styles.panel}>
        <div className={styles.tableScroll}>
          <table className={styles.dataTable}>
            <thead>
              <tr>
                {shownCols.map((c) => {
                  const sorted = sort.key === c.key;
                  const cf = c.filterKey ? colFilters[c.filterKey] : null;
                  return (
                    <th
                      key={c.key}
                      className={`${styles.thCell} ${c.num ? styles.num : ""} ${sorted ? styles.sorted : ""}`}
                    >
                      <span className={styles.thIn}>
                        <button
                          type="button"
                          className={styles.thSort}
                          onClick={() => toggleSort(c.key)}
                          title="Sort"
                        >
                          <span className={styles.thLbl}>{c.label}</span>
                          <span className={styles.thArrow}>
                            {sorted ? (sort.dir === "asc" ? "↑" : "↓") : ""}
                          </span>
                        </button>
                        {c.filterKey && (
                          <button
                            type="button"
                            className={`${styles.thFilter} ${cf?.size ? styles.filterOn : ""}`}
                            onClick={openHF(c.filterKey)}
                            aria-label={`Filter ${c.label}`}
                          >
                            {ICON_FUNNEL}
                          </button>
                        )}
                      </span>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {tableRows.map((a) => (
                <tr key={a.adId}>{shownCols.map((c) => c.cell(a))}</tr>
              ))}
              {tableRows.length === 0 && (
                <tr>
                  <td className={styles.empty} colSpan={shownCols.length}>
                    {funnel.isLoading
                      ? "Loading…"
                      : rows.length === 0
                        ? "No attributed chats yet. Rows appear when customers message via a click-to-WhatsApp ad on the connected number — each row is an ad with its chats, orders and ROAS."
                        : "No ads match these filters."}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* header-filter dropdown (fixed, never clips; bottom-sheet on phones) */}
      {hf && (
        <>
          <button
            type="button"
            className={styles.menuCatch}
            aria-label="Close filter"
            onClick={() => setHf(null)}
          />
          <div
            className={`${styles.filterMoreMenu} ${styles.hfMenu}`}
            style={{ position: "fixed", left: hf.x, top: hf.y }}
          >
            <div className={styles.colMenuH}>Filter {hf.key}</div>
            {FILTER_VALUES[hf.key].map((v) => {
              const on = colFilters[hf.key].has(v);
              return (
                <button
                  key={v}
                  type="button"
                  className={`${styles.moreOpt} ${styles.segOpt} ${on ? styles.optOn : ""}`}
                  onClick={() => toggleCF(hf.key, v)}
                >
                  <span className={styles.colCheck}>{on ? ICON_CHECK : null}</span>
                  {hf.key === "status" ? STATUS_LABEL[v] : v}
                </button>
              );
            })}
            {colFilters[hf.key].size > 0 && (
              <button
                type="button"
                className={styles.hfClear}
                onClick={() => {
                  clearCF(hf.key);
                  setHf(null);
                }}
              >
                Clear filter
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}
